import { toast } from 'react-hot-toast';

/**
 * 统一错误处理系统
 * 提供错误分类、用户友好消息和统一的错误处理接口
 * 包含成功、警告和错误消息的统一处理
 */

// 消息类型枚举
export const MESSAGE_TYPES = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  INFO: 'info'
};

// 错误类型枚举 - 合并了旧系统和新系统的所有错误类型
export const ERROR_TYPES = {
  // 内容策略相关
  CONTENT_POLICY: 'content_policy',
  SAFETY_POLICY: 'safety_policy', 
  SAFETY_VIOLATION: 'safety', // 兼容旧系统
  RECITATION: 'recitation',
  RECITATION_CONCERN: 'recitation', // 兼容旧系统
  
  // 资源限制相关
  RATE_LIMIT: 'rate_limit',
  CREDITS: 'credits',
  QUOTA_EXCEEDED: 'quota_exceeded',
  
  // 生成相关
  NO_IMAGE: 'no_image',
  NO_IMAGE_GENERATED: 'no_image', // 兼容旧系统
  GENERATION_FAILED: 'generation_failed',
  PROMPT_TOO_COMPLEX: 'prompt_complex',
  TIMEOUT: 'timeout',
  GENERATE_PROCESSING: 'GENERATE_PROCESSING',
  
  // 网络和服务器相关
  NETWORK_ERROR: 'network_error',
  NETWORK: 'network_error', // 兼容旧系统，统一为network_error
  SERVER_ERROR: 'server_error',
  SERVER: 'server_error', // 兼容旧系统，统一为server_error
  AUTH_ERROR: 'auth_error',
  AUTH: 'auth_error', // 兼容旧系统，统一为auth_error
  NOT_FOUND: 'not_found',
  API_ERROR: 'api_error',
  
  // 输入验证相关
  INVALID_INPUT: 'invalid_input',
  MISSING_REQUIRED: 'missing_required',
  
  // 通用错误
  UNKNOWN: 'unknown'
};

// 错误严重程度
export const ERROR_SEVERITY = {
  LOW: 'low',      // 信息提示
  MEDIUM: 'medium', // 警告
  HIGH: 'high',    // 错误
  CRITICAL: 'critical' // 严重错误
};

/**
 * 错误分类函数 - 增强版，支持更多错误类型
 * @param {string|Object} errorMessage 错误消息
 * @returns {Object} 包含错误类型和严重程度的对象
 */
export const classifyError = (errorMessage) => {
  let message = '';
  const responseData = errorMessage?.response?.data;

  // 优先检查错误码 (Case insensitive check)
  const code = responseData?.code;
  const error = responseData?.error;
  if (code && (code === 'INSUFFICIENT_CREDITS' || code === 'insufficient_credits' || code === 402 || (typeof error === 'string' && error.includes('Insufficient credits')))) {
    return { type: ERROR_TYPES.CREDITS, severity: ERROR_SEVERITY.MEDIUM };
  }

  // 提取错误信息 - 聚合所有可能的错误信息源
  let searchParts = [];

  if (typeof errorMessage === 'string') {
    searchParts.push(errorMessage);
  }
  
  if (responseData) {
    if (typeof responseData === 'object') {
       // 优先添加明确的错误字段
       if (responseData.error) searchParts.push(String(responseData.error));
       if (responseData.message) searchParts.push(String(responseData.message));
       // 添加完整JSON字符串以防万一
       try {
         searchParts.push(JSON.stringify(responseData));
       } catch (e) {
         // ignore circular reference etc
       }
    } else {
       searchParts.push(String(responseData));
    }
  }
  
  if (errorMessage?.message) {
    searchParts.push(errorMessage.message);
  }

  // 如果没有找到任何信息，使用默认转换
  if (searchParts.length === 0) {
    searchParts.push(String(errorMessage));
  }
  
  message = searchParts.join(' ').toLowerCase();

  // 根据关键词分类错误

  // 根据关键词分类错误
  switch (true) {
    // 内容政策相关
    case message.includes('content policy') || 
         message.includes('content violation') ||
         message.includes('policy violation'):
      return { type: ERROR_TYPES.CONTENT_POLICY, severity: ERROR_SEVERITY.HIGH };
    
    case message.includes('safety') || 
         message.includes('harmful') ||
         message.includes('inappropriate'):
      return { type: ERROR_TYPES.SAFETY_POLICY, severity: ERROR_SEVERITY.HIGH };
    
    case message.includes('recitation') ||
         message.includes('copyright'):
      return { type: ERROR_TYPES.RECITATION, severity: ERROR_SEVERITY.MEDIUM };

    // 资源限制相关
    case message.includes('rate limit') || 
         message.includes('too many requests') ||
         message.includes('quota exceeded'):
      return { type: ERROR_TYPES.RATE_LIMIT, severity: ERROR_SEVERITY.MEDIUM };
    
    case message.includes('credits') || 
         message.includes('insufficient balance') ||
         message.includes('no credits') ||
         message.includes('积分不足'):
      return { type: ERROR_TYPES.CREDITS, severity: ERROR_SEVERITY.MEDIUM };

    // 生成相关
    case message.includes('no image') || 
         message.includes('generation failed') ||
         message.includes('failed to generate') ||
         message.includes('Unable to generate image for this prompt'):
      return { type: ERROR_TYPES.NO_IMAGE, severity: ERROR_SEVERITY.MEDIUM };
    
    case message.includes('prompt too complex') ||
         message.includes('complex prompt') ||
         message.includes('prompt complexity'):
      return { type: ERROR_TYPES.PROMPT_TOO_COMPLEX, severity: ERROR_SEVERITY.MEDIUM };
    
    case message.includes('timeout') || 
         message.includes('request timeout') ||
         message.includes('timed out'):
      return { type: ERROR_TYPES.TIMEOUT, severity: ERROR_SEVERITY.MEDIUM };

    // 网络和服务器相关
    case message.includes('network') || 
         message.includes('connection') ||
         message.includes('fetch'):
      return { type: ERROR_TYPES.NETWORK_ERROR, severity: ERROR_SEVERITY.MEDIUM };
    
    case message.includes('server error') || 
         message.includes('internal server') ||
         message.includes('500'):
      return { type: ERROR_TYPES.SERVER_ERROR, severity: ERROR_SEVERITY.HIGH };
    
    case message.includes('unauthorized') || 
         message.includes('authentication') ||
         message.includes('401'):
      return { type: ERROR_TYPES.AUTH_ERROR, severity: ERROR_SEVERITY.HIGH };
    
    case message.includes('not found') || 
         message.includes('404'):
      return { type: ERROR_TYPES.NOT_FOUND, severity: ERROR_SEVERITY.MEDIUM };
    
    case message.includes('api error') ||
         message.includes('api') ||
         message.includes('service unavailable'):
      return { type: ERROR_TYPES.API_ERROR, severity: ERROR_SEVERITY.HIGH };

    // 输入验证相关
    case message.includes('invalid input') || 
         message.includes('validation') ||
         message.includes('invalid'):
      return { type: ERROR_TYPES.INVALID_INPUT, severity: ERROR_SEVERITY.LOW };
    
    case message.includes('required') || 
         message.includes('missing'):
      return { type: ERROR_TYPES.MISSING_REQUIRED, severity: ERROR_SEVERITY.LOW };

    case message.includes('110'):
      return { type: ERROR_TYPES.GENERATE_PROCESSING, severity: ERROR_SEVERITY.LOW };

    // 默认情况
    default:
      return { type: ERROR_TYPES.UNKNOWN, severity: ERROR_SEVERITY.MEDIUM };
  }
};

/**
 * 获取错误图标 - 支持所有错误类型
 * @param {string} errorType 错误类型
 * @param {string} severity 错误严重程度
 * @returns {string} 错误图标
 */
export const getErrorIcon = (errorType, severity) => {
  switch (errorType) {
    case ERROR_TYPES.CONTENT_POLICY:
    case ERROR_TYPES.SAFETY_POLICY:
    case ERROR_TYPES.SAFETY_VIOLATION:
      return '🚫';
    case ERROR_TYPES.RECITATION:
    case ERROR_TYPES.RECITATION_CONCERN:
      return '©️';
    case ERROR_TYPES.RATE_LIMIT:
      return '⏱️';
    case ERROR_TYPES.CREDITS:
    case ERROR_TYPES.QUOTA_EXCEEDED:
      return '💳';
    case ERROR_TYPES.TIMEOUT:
      return '⏰';
    case ERROR_TYPES.NO_IMAGE:
    case ERROR_TYPES.NO_IMAGE_GENERATED:
      return '🖼️';
    case ERROR_TYPES.PROMPT_TOO_COMPLEX:
      return '🤔';
    case ERROR_TYPES.NETWORK_ERROR:
    case ERROR_TYPES.NETWORK:
      return '🌐';
    case ERROR_TYPES.SERVER_ERROR:
    case ERROR_TYPES.SERVER:
      return '🔧';
    case ERROR_TYPES.AUTH_ERROR:
    case ERROR_TYPES.AUTH:
      return '🔐';
    case ERROR_TYPES.NOT_FOUND:
      return '🔍';
    case ERROR_TYPES.API_ERROR:
      return '🔌';
    case ERROR_TYPES.INVALID_INPUT:
    case ERROR_TYPES.MISSING_REQUIRED:
      return '📝';
    default:
      return severity === ERROR_SEVERITY.CRITICAL ? '🚨' : '⚠️';
  }
};

/**
 * 获取用户友好的错误消息 - 支持国际化
 * @param {string} errorType 错误类型
 * @param {Function} t 翻译函数
 * @param {string} originalMessage 原始错误消息
 * @returns {string} 用户友好的错误消息
 */
export const getFriendlyErrorMessage = (errorType, t, originalMessage = '') => {
  // 尝试获取统一错误处理系统的翻译
  const translationKey = `unifiedErrorHandler.${errorType}`;
  const translatedMessage = t(translationKey);
  
  // 如果翻译键存在且不等于原始键，返回翻译后的消息
  if (translatedMessage && translatedMessage !== translationKey) {
    return translatedMessage;
  }
  
  // 回退到旧系统的翻译
  const fallbackKey = `errorHandler.${errorType}`;
  const fallbackMessage = t(fallbackKey);
  
  if (fallbackMessage && fallbackMessage !== fallbackKey) {
    return fallbackMessage;
  }
  
  // 如果都没有翻译，返回默认消息
  const defaultMessages = {
    content_policy: 'Content violates platform policy, please modify and try again',
    safety_policy: 'Content involves safety issues, please modify the prompt and try again',
    safety: 'Content involves safety issues, please modify the prompt and try again',
    recitation: 'Content may involve copyright issues, please modify the prompt and try again',
    rate_limit: 'Too many requests, please try again later',
    credits: 'Insufficient credits, please recharge and try again',
    quota_exceeded: 'Quota exceeded, please try again later',
    no_image: 'Failed to generate image, please try modifying the prompt',
    generation_failed: 'Generation failed, please try again',
    prompt_complex: 'Prompt is too complex, please simplify and try again',
    GENERATE_PROCESSING: 'You have an ongoing generation task, please wait.',
    timeout: 'Operation timed out, please try again',
    network_error: 'Network connection failed, please check your connection',
    server_error: 'Internal server error, please try again later',
    auth_error: 'Authentication failed, please log in again',
    not_found: 'Resource not found',
    api_error: 'API call failed, please try again later',
    invalid_input: 'Invalid input, please check and try again',
    missing_required: 'Please fill in required fields',
    unknown: 'Unknown error occurred, please try again'
  };
  
  return defaultMessages[errorType] || originalMessage || 'An error occurred, please try again';
};

/**
 * 统一错误处理器
 * @param {string|Object|Error} error 错误对象或消息
 * @param {Function} t 翻译函数
 * @param {Object} options 选项
 * @param {boolean} options.showToast 是否显示toast提示
 * @param {number} options.duration toast显示时长
 * @param {Function} options.onError 错误回调
 * @returns {Object} 处理后的错误信息
 */
export const handleUnifiedError = (error, t, options = {}) => {
  const {
    showToast = true,
    duration = 6000,
    onError
  } = options;

  let errorMessage = '';
  
  // 提取错误消息
  if (error instanceof Error) {
    // 优先尝试从响应数据中获取错误信息
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else {
      errorMessage = error.message;
    }
  } else if (typeof error === 'object' && error !== null) {
    errorMessage = error.message || error.errorMessage || JSON.stringify(error);
  } else {
    errorMessage = error?.toString() || '';
  }

  // 分类错误 - 传递原始错误对象以便获取更多上下文（如错误码）
  const { type, severity } = classifyError(error);
  
  // 获取用户友好的消息
  const friendlyMessage = getFriendlyErrorMessage(type, t, errorMessage);
  
  // 获取图标
  const icon = getErrorIcon(type, severity);

  // 构建错误信息对象
  const errorInfo = {
    type,
    severity,
    message: friendlyMessage,
    originalMessage: errorMessage,
    icon,
    timestamp: new Date().toISOString()
  };

  // 显示toast提示
  if (showToast) {
    const toastStyle = {
      background: '#2a2a2e',
      color: '#fff',
      border: '1px solid #333',
      maxWidth: '100%',
      wordWrap: 'break-word'
    };

    // 根据严重程度调整样式
    if (severity === ERROR_SEVERITY.CRITICAL) {
      toastStyle.background = '#d32f2f';
    } else if (severity === ERROR_SEVERITY.HIGH) {
      toastStyle.background = '#f57c00';
    }

    toast.error(friendlyMessage, {
      duration,
      style: toastStyle,
      icon: icon,
    });
  }

  // 调用错误回调
  if (onError && typeof onError === 'function') {
    onError(errorInfo);
  }

  // 记录错误日志
  console.error('Unified Error Handler:', {
    type,
    severity,
    friendlyMessage,
    originalMessage: errorMessage,
    timestamp: errorInfo.timestamp
  });

  return errorInfo;
};

/**
 * 创建错误处理器工厂函数
 * @param {Function} t 翻译函数
 * @param {Object} defaultOptions 默认选项
 * @returns {Function} 错误处理器函数
 */
export const createErrorHandler = (t, defaultOptions = {}) => {
  return (error, options = {}) => {
    return handleUnifiedError(error, t, { ...defaultOptions, ...options });
  };
};

/**
 * 显示成功消息
 * @param {string} message 成功消息
 * @param {Object} options 选项
 * @param {number} options.duration 显示时长
 * @param {string} options.icon 自定义图标
 * @param {Object} options.style 自定义样式
 */
export const showSuccessToast = (message, options = {}) => {
  const {
    duration = 4000,
    icon = '✅',
    style = {}
  } = options;

  const defaultStyle = {
    background: '#4caf50',
    color: '#fff',
    border: '1px solid #45a049',
    maxWidth: '100%',
    wordWrap: 'break-word'
  };

  toast.success(message, {
    duration,
    icon,
    style: { ...defaultStyle, ...style }
  });
};

/**
 * 显示警告消息
 * @param {string} message 警告消息
 * @param {Object} options 选项
 * @param {number} options.duration 显示时长
 * @param {string} options.icon 自定义图标
 * @param {Object} options.style 自定义样式
 */
export const showWarningToast = (message, options = {}) => {
  const {
    duration = 5000,
    icon = '⚠️',
    style = {}
  } = options;

  const defaultStyle = {
    background: '#ff9800',
    color: '#fff',
    border: '1px solid #f57c00',
    maxWidth: '100%',
    wordWrap: 'break-word'
  };

  toast(message, {
    duration,
    icon,
    style: { ...defaultStyle, ...style }
  });
};

/**
 * 显示信息消息
 * @param {string} message 信息消息
 * @param {Object} options 选项
 * @param {number} options.duration 显示时长
 * @param {string} options.icon 自定义图标
 * @param {Object} options.style 自定义样式
 */
export const showInfoToast = (message, options = {}) => {
  const {
    duration = 4000,
    icon = 'ℹ️',
    style = {}
  } = options;

  const defaultStyle = {
    background: '#2196f3',
    color: '#fff',
    border: '1px solid #1976d2',
    maxWidth: '100%',
    wordWrap: 'break-word'
  };

  toast(message, {
    duration,
    icon,
    style: { ...defaultStyle, ...style }
  });
};

export const unifiedErrorHandlerModule = {
  MESSAGE_TYPES,
  ERROR_TYPES,
  ERROR_SEVERITY,
  classifyError,
  getErrorIcon,
  getFriendlyErrorMessage,
  handleUnifiedError,
  createErrorHandler,
  showSuccessToast,
  showWarningToast,
  showInfoToast
};

export default unifiedErrorHandlerModule;